'use client';

import React, { useState, useEffect, useMemo } from 'react';
import { ThemeToggle } from '../Theme/theme-toggle';
import { useLoginModals } from '../Login/common/LoginModalsManager';
import { useAuthUser } from '@/lib/auth/common/useSession';
import { useLogoutConfirmStore } from '@/store/store';

interface UtilityNavData {
  announcement_bar_desktop?: {
    announcement_text?: string;
    show_announcement?: boolean;
  };
  announcement_bar_mobile?: {
    announcement_text?: string;
    show_announcement?: boolean;
  };
  url?: string;
  upper_right_menus?: Array<{
    menu_link: {
      title: string;
      href?: string;
    };
    show_this_button?: boolean;
    open_in_new_tab?: boolean;
    _metadata?: {
      uid?: string;
    };
  }>;
}

interface AnnouncementBarProps {
  utilityNavData?: UtilityNavData;
  storageKeyDesktop?: string;
  storageKeyMobile?: string;
  dismissible?: boolean;
  /** Server-resolved login state from cookie; avoids Login/Logout text flicker */
  initialIsLoggedIn?: boolean;
}

export function AnnouncementBar({
  utilityNavData,
  storageKeyDesktop = 'announcement-desktop-dismissed',
  storageKeyMobile = 'announcement-mobile-dismissed',
  dismissible = true,
  initialIsLoggedIn = false,
}: AnnouncementBarProps) {
  // Extract data from utilityNavData
  const announcementTextDesktop = utilityNavData?.announcement_bar_desktop?.announcement_text;
  const announcementTextMobile = utilityNavData?.announcement_bar_mobile?.announcement_text;
  const url = utilityNavData?.url;
  const showAnnouncementDesktop = utilityNavData?.announcement_bar_desktop?.show_announcement ?? false;
  const showAnnouncementMobile = utilityNavData?.announcement_bar_mobile?.show_announcement ?? false;
  
  const getInitialVisibility = (showAnnouncement: boolean, storageKey: string) => {
    if (!showAnnouncement) return { visible: false, dismissed: false };
    if (!dismissible) return { visible: true, dismissed: false };
    if (typeof window === 'undefined') return { visible: true, dismissed: false };
    const dismissed = localStorage.getItem(storageKey) === 'true';
    return { visible: !dismissed, dismissed };
  };

  const [isVisibleDesktop, setIsVisibleDesktop] = useState(() =>
    getInitialVisibility(showAnnouncementDesktop, storageKeyDesktop).visible
  );
  const [isDismissedDesktop, setIsDismissedDesktop] = useState(() =>
    getInitialVisibility(showAnnouncementDesktop, storageKeyDesktop).dismissed
  );
  const [isVisibleMobile, setIsVisibleMobile] = useState(() =>
    getInitialVisibility(showAnnouncementMobile, storageKeyMobile).visible
  );
  const [isDismissedMobile, setIsDismissedMobile] = useState(() =>
    getInitialVisibility(showAnnouncementMobile, storageKeyMobile).dismissed
  );
  // Canonical auth state (see lib/auth/common/useSession.ts) — single reactive source,
  // replaces the old isUserLoggedIn() mount seed + 1s poll + storage/event effects.
  const { isLoggedIn: authLoggedIn, isLoading: authLoading } = useAuthUser();
  // Shared with MainNavigation (see store/store.ts useLogoutConfirmStore) — one modal
  // instance app-wide, mounted once in app/layout.tsx, instead of a separate copy here.
  const openLogoutConfirm = useLogoutConfirmStore((s) => s.open);

  // Derived, not synced via an effect: purely a function of authLoggedIn/authLoading,
  // with initialIsLoggedIn as the server-seeded value shown until the hook resolves.
  const isLoggedIn = authLoading ? initialIsLoggedIn : authLoggedIn;

  // Get login modals from context
  let loginModals: { openLoginModal: () => void; closeLoginModal: () => void } | null = null;
  try {
    loginModals = useLoginModals();
  } catch {
    // Context not available, will handle gracefully
  }

  // Initialize announcement visibility
  useEffect(() => {
    const checkVisibility = (showAnnouncement: boolean, storageKey: string) => {
      if (!showAnnouncement) return { visible: false, dismissed: false };
      if (!dismissible) return { visible: true, dismissed: false };
      
      const dismissed = localStorage.getItem(storageKey) === 'true';
      return { visible: !dismissed, dismissed };
    };

    const desktop = checkVisibility(showAnnouncementDesktop, storageKeyDesktop);
    const mobile = checkVisibility(showAnnouncementMobile, storageKeyMobile);

    setIsVisibleDesktop(desktop.visible);
    setIsDismissedDesktop(desktop.dismissed);
    setIsVisibleMobile(mobile.visible);
    setIsDismissedMobile(mobile.dismissed);
  }, [showAnnouncementDesktop, showAnnouncementMobile, dismissible, storageKeyDesktop, storageKeyMobile]);

  const trimText = (text: string, maxChars: number = 80): string => {
    if (!text) return '';
    return text.length > maxChars ? text.slice(0, maxChars) + '...' : text;
  };

  const isLoginLink = (link: { menu_link?: { href?: string; title?: string } }): boolean => {
    if (!link?.menu_link) return false;
    const title = link.menu_link.title?.toLowerCase() || '';
    const href = link.menu_link.href?.toLowerCase() || '';
    return title.includes('login') || href.includes('login');
  };

  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>, link: any) => {
    if (isLoginLink(link)) {
      e.preventDefault();
      loginModals?.openLoginModal();
    }
  };

  const handleLogoutClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    openLogoutConfirm();
  };

  // Transform link data: replace Academy with Contentstack.com
  const transformLinkData = (link: any) => {
    const originalTitle = link?.menu_link?.title || '';
    const isAcademyLink = originalTitle.toLowerCase().includes('academy');
    
    if (isAcademyLink) {
      return {
        ...link,
        menu_link: {
          ...link.menu_link,
          title: originalTitle.replace(/Academy/gi, 'Contentstack.com'),
          href: 'https://www.contentstack.com/',
        },
        open_in_new_tab: true,
        isContentstackLink: true,
      };
    }
    
    return link;
  };

  // Filter and prepare utility menu links
  const utilityLinks = useMemo(() => {
    if (!utilityNavData?.upper_right_menus) return [];
    
    return utilityNavData.upper_right_menus.map((link: any) => {
      // Transform Academy links to Contentstack.com
      let transformedLink = transformLinkData(link);
      
      // If logged in and this is a login link, transform it to logout
      if (isLoginLink(transformedLink) && isLoggedIn) {
        transformedLink = {
          ...transformedLink,
          menu_link: {
            ...transformedLink.menu_link,
            title: 'Logout',
          },
          isLogout: true,
        };
      }
      
      return transformedLink;
    }).filter((link: any) => {
      if (!link?.show_this_button) return false;
      return true;
    });
  }, [utilityNavData?.upper_right_menus, isLoggedIn]);

  const announcementContentDesktop = announcementTextDesktop ? (
    <div className="group flex items-center gap-2 cs-caption text-text-action-strong">
      <span className="group-hover:underline transition-all text-text-action-strong truncate overflow-hidden">
        {trimText(announcementTextDesktop)}
      </span>
      <div className="flex items-center justify-center bg-transparent text-lg transition-all duration-200 group-hover:translate-x-2 flex-shrink-0 pr-2 text-text-action-strong">
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
          <path d="M7.10208 5.25H0V4.08333H7.10208L3.83542 0.816667L4.66667 0L9.33333 4.66667L4.66667 9.33333L3.83542 8.51667L7.10208 5.25Z" fill="currentColor" />
        </svg>
      </div>
    </div>
  ) : null;

  const announcementContentMobile = announcementTextMobile ? (
    <div className="group flex items-center gap-1.5 sm:gap-2 cs-caption text-text-action-strong">
      <span className="group-hover:underline transition-all text-text-action-strong break-words sm:truncate tab:truncate overflow-hidden flex-1 min-w-0">
        {trimText(announcementTextMobile)}
      </span>
      <span className="flex items-center justify-center bg-transparent text-lg transition-all duration-200 group-hover:translate-x-1 sm:group-hover:translate-x-2 flex-shrink-0 sm:pr-2 tab:pr-2 self-center text-text-action-strong">
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
          <path d="M7.10208 5.25H0V4.08333H7.10208L3.83542 0.816667L4.66667 0L9.33333 4.66667L4.66667 9.33333L3.83542 8.51667L7.10208 5.25Z" fill="currentColor" />
        </svg>
      </span>
    </div>
  ) : null;

  return (
    <>
      {/* Mobile/Tablet Announcement Bar */}
      <div 
        className="sticky top-0 relative bg-custom-nav-gradient flex text-sm text-text-default-strong max-[1499px]:flex hidden z-0"
        data-swiftype-index="false"
      >
        <div className="section py-3 tablet:py-3 sm:py-4 relative flex w-full items-start sm:items-center gap-3">
          {/* Left side - Announcement bar - only show on mobile, hide on tablet */}
          {(isVisibleMobile && !isDismissedMobile && announcementContentMobile) && (
            <div className="flex-1 min-w-0 pr-3 sm:pr-4 tab:pr-[180px] max-w-[calc(100%-100px)] sm:max-w-[calc(100%-120px)] tab:max-w-[calc(100%-180px)] overflow-hidden">
              {url ? (
                <a href={url} className="flex items-center w-full overflow-hidden min-w-0">
                  {announcementContentMobile}
                </a>
              ) : (
                <div className="flex items-center w-full overflow-hidden min-w-0">{announcementContentMobile}</div>
              )}
            </div>
          )}
          
          {/* Right side container - align to section padding: 16px mobile, 32px tablet, 72px desktop */}
          <div className="absolute right-4 tab:right-8 lg:right-[72px] top-1/2 -translate-y-1/2 flex items-center gap-3 tablet:gap-4 md:gap-4 flex-shrink-0 whitespace-nowrap z-10">
            {/* Mobile view - show only login link (no theme icon) */}
            {utilityLinks.length > 0 && (() => {
              const lastLink = utilityLinks[utilityLinks.length - 1];
              const isLogin = isLoginLink(lastLink);
              const isLogout = lastLink?.isLogout;
              return (
                <a
                  href={isLogin || isLogout ? '#' : lastLink?.menu_link?.href || '#'}
                  target={lastLink?.open_in_new_tab ? '_blank' : '_self'}
                  onClick={(e) => {
                    if (isLogout) {
                      handleLogoutClick(e);
                    } else {
                      handleLinkClick(e, lastLink);
                    }
                  }}
                  className="text-xs text-text-default-light transition-colors hover:text-text-default-strong cursor-pointer sm:hidden"
                  data-test-id="utility-nav-link"
                >
                  {lastLink?.menu_link?.title}
                </a>
              );
            })()}
            
            {/* Tablet view (sm and up, but below lg) - show login link and theme icon */}
            <div className="hidden items-center gap-4 sm:flex lg:hidden md:gap-4">
              {utilityLinks.length > 0 && (() => {
                const lastLink = utilityLinks[utilityLinks.length - 1];
                const isLogin = isLoginLink(lastLink);
                const isLogout = lastLink?.isLogout;
                return (
                  <a
                    href={isLogin || isLogout ? '#' : lastLink?.menu_link?.href || '#'}
                    target={lastLink?.open_in_new_tab ? '_blank' : '_self'}
                    onClick={(e) => {
                      if (isLogout) {
                        handleLogoutClick(e);
                      } else {
                        handleLinkClick(e, lastLink);
                      }
                    }}
                    className="text-xs text-text-default-light transition-colors hover:text-text-default-strong cursor-pointer"
                    data-test-id="utility-nav-link"
                  >
                    {lastLink?.menu_link?.title}
                  </a>
                );
              })()}
              <ThemeToggle />
            </div>
          </div>
        </div>
      </div>

      {/* Desktop Announcement Bar */}
      <div className="bg-custom-nav-gradient relative hidden w-full items-center justify-center min-[1500px]:flex" data-swiftype-index="false">
        <div className="section py-[13.5px] tablet:py-4 flex w-full items-center gap-6">
          {/* Announcement content - only show if visible and not dismissed */}
          {(isVisibleDesktop && !isDismissedDesktop && announcementContentDesktop) && (
            <div className="flex-1 min-w-0 pr-8 max-w-[calc(100%-180px)]">
              {url ? (
                <a href={url} className="inline-block">
                  {announcementContentDesktop}
                </a>
              ) : (
                <div className="inline-block">{announcementContentDesktop}</div>
              )}
            </div>
          )}

          {/* Utility links */}
          <div className="ml-auto hidden items-center cs-caption gap-4 text-text-default-light lg:flex lg:gap-6 flex-shrink-0 whitespace-nowrap">
            {utilityLinks.map((link: any, index: number) => {
              const isLogin = isLoginLink(link);
              const isLogout = link?.isLogout;
              const isContentstackLink = link?.isContentstackLink;
              
              return (
                <a
                  key={link?._metadata?.uid || index}
                  href={isLogin || isLogout ? '#' : link?.menu_link?.href || '#'}
                  target={link?.open_in_new_tab ? '_blank' : '_self'}
                  onClick={(e) => {
                    if (isLogout) {
                      handleLogoutClick(e);
                    } else if (!isContentstackLink) {
                      handleLinkClick(e, link);
                    }
                    // For Contentstack.com link, let it navigate naturally
                  }}
                  className="hover:text-text-default-strong cursor-pointer"
                  data-test-id="utility-nav-link"
                >
                  {link?.menu_link?.title}
                </a>
              );
            })}
          </div>
          <ThemeToggle />
        </div>
      </div>

      {/* Logout confirmation modal is shared app-wide — see LogoutConfirmationController in app/layout.tsx */}
    </>
  );
}